import React, { useEffect, useState } from "react";
import { Button, Col, InputGroup, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useRouteLoaderData } from "react-router-dom";

import { GamePublicationState } from "@/const";
import { useRequest } from "@components/AppHook";
import {
    DevForm,
    FormFieldErrorMessage,
    FormFieldGroup,
    FormFieldHelpText,
    FormFieldLenCount,
} from "@components/Forms/Fields";
import { fieldChange } from "@components/Forms/Handlers";
import { Awesome, DevModal } from "@components/Helpers";
import { url_reverse } from "@components/Utils";

export function LocalizersDataField({ apiData, setApiData, prefix, name, from_admin_page = false }) {
    const { t } = useTranslation();
    const [show, setShow] = useState(false);
    const [data, setData] = useState({});
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const { game } = gameCommonInfo;
    const field_name = `${prefix}-${name}`;

    useEffect(() => {
        if (apiData.forms_data && apiData.forms_data[prefix].data[name]) {
            setData(JSON.parse(apiData.forms_data[prefix].data[name]));
        }
    }, []);

    if (!apiData?.forms_data) {
        return null;
    }

    const form = apiData.forms_data[prefix];
    const field = form.fields[name];
    if (!field) {
        return null;
    }
    const isDisabled = Boolean(
        field.disabled ||
            (!from_admin_page &&
                (game.publication_state === GamePublicationState.TESTING ||
                    game.publication_state === GamePublicationState.TESTING_MARKETING))
    );

    const dataChange = (loc, k, val) => {
        const newData = { ...data };
        if (!(loc in newData)) {
            newData[loc] = {};
        }
        newData[loc][k] = val;
        setData(newData);
    };

    const modalClose = () => {
        setShow(false);
    };

    const onApply = () => {
        const newApiData = { ...apiData };
        newApiData.forms_data[prefix].data[name] = JSON.stringify(data);
        setApiData(newApiData);
        modalClose();
    };

    return field.widget.is_hidden ? (
        ""
    ) : (
        <>
            <FormFieldGroup label={field.label} required={field.required}>
                <div className={`${field_name}-anchr`}>
                    <Button
                        variant="dark"
                        disabled={isDisabled}
                        onClick={() => {
                            setShow(true);
                        }}
                    >
                        {t("Изменить")}
                    </Button>
                </div>
            </FormFieldGroup>
            <DevModal
                size="lg"
                show={show}
                onHide={modalClose}
                header={t("Поддерживаемые языки")}
                body={Object.keys(apiData.localizers_dict).map((loc) => {
                    return (
                        <Row key={`localizers_data_${loc}`}>
                            <Col md={3}>{apiData.localizers_dict[loc]}</Col>
                            <Col md={9}>
                                <DevForm.Checkbox
                                    inline
                                    label={t("Озвучка")}
                                    checked={loc in data && data[loc].sound === "1"}
                                    onChange={(e) => {
                                        dataChange(loc, "sound", e.target.checked ? "1" : "0");
                                    }}
                                />
                                <DevForm.Checkbox
                                    inline
                                    label={t("Текст")}
                                    checked={loc in data && data[loc].text === "1"}
                                    onChange={(e) => {
                                        dataChange(loc, "text", e.target.checked ? "1" : "0");
                                    }}
                                />
                            </Col>
                        </Row>
                    );
                })}
                footer={
                    <Button variant="primary" onClick={onApply}>
                        {t("Применить")}
                    </Button>
                }
            />
        </>
    );
}

export function IframeVersionIncField({ apiData, setApiData }) {
    const { t } = useTranslation();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const { api_post } = useRequest();

    if (!apiData?.forms_data) {
        return null;
    }

    const prefix = "main";
    const name = "iframe_version_inc";
    const form = apiData.forms_data[prefix];
    const field = form.fields[name];
    if (!field) {
        return null;
    }

    const { game } = gameCommonInfo;

    const incrementVersion = (ev) => {
        api_post(ev, url_reverse("api:game:inc_iframe_version", { game_id: game.id }), {
            params: { game_id: game.id },
            then: (resp) => {
                if (resp.status === "ok" && "iframe_landing_url" in resp) {
                    const newApiData = { ...apiData };
                    newApiData.forms_data.main.data.iframe_landing_url = resp.iframe_landing_url;
                    setApiData(newApiData);
                }
            },
        });
    };

    return field.widget.is_hidden ? (
        ""
    ) : (
        <FormFieldGroup label={field.label} required={field.required}>
            <DevForm.Button variant="dark" name="inc_version" onClick={incrementVersion} disabled={field.disabled}>
                {t("Повысить")}
            </DevForm.Button>
        </FormFieldGroup>
    );
}

export function LPIframeLandingUrlsField({ apiData, setApiData }) {
    const { t } = useTranslation();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    const onAdd = () => {
        const num = apiData.lpifrurls.slice(-1)[0] ? apiData.lpifrurls.slice(-1)[0].num : 0;
        const newApiData = { ...apiData };
        newApiData.lpifrurls.push({ num: num + 1, url: "" });
        setApiData(newApiData);
    };

    useEffect(() => {
        if (apiData.lpifrurls.length === 0) {
            onAdd();
        }
    }, []);

    if (!apiData?.forms_data) {
        return null;
    }

    const prefix = "main";
    const name = "lp_iframe_landing_urls";
    const form = apiData.forms_data[prefix];
    const field = form.fields[name];
    if (!field) {
        return null;
    }
    const { game } = gameCommonInfo;

    const onChange = (object, val) => {
        const newApiData = { ...apiData };
        for (const x of newApiData.lpifrurls) {
            if (x.num === object.num) {
                x.url = val;
            }
        }
        setApiData(newApiData);
    };

    const onDel = (object) => {
        const newApiData = { ...apiData };
        newApiData.lpifrurls = newApiData.lpifrurls.filter((o) => {
            return o.num !== object.num;
        });
        setApiData(newApiData);
    };

    return field.widget.is_hidden ? (
        ""
    ) : (
        <>
            <FormFieldGroup label={field.label} required={field.required}>
                {apiData.lpifrurls.map((o, i) => {
                    return (
                        <div className="dev-inline-control mb-1" key={`lpifrurls_${i}`}>
                            <DevForm.TextInput
                                size="sm"
                                value={o.url}
                                placeholder={t("Ссылка")}
                                maxLength={255}
                                onChange={(ev) => onChange(o, ev.target.value)}
                            />
                            {i === 0 && (
                                <Button variant="light" size="sm" onClick={onAdd}>
                                    <Awesome type="plus" />
                                </Button>
                            )}
                            {i !== 0 && (
                                <Button variant="danger" size="sm" onClick={() => onDel(o)}>
                                    <Awesome type="minus" />
                                </Button>
                            )}
                            {game?.is_mini_browser && o.id && (
                                <a
                                    className="btn btn-sm btn-info"
                                    href={`https://mini.vkplay.ru/play/game/${game.slug}/lp/${o.num}/?draft=true`}
                                    target="_blank"
                                >
                                    <Awesome type="globe" tooltip={t("Открыть в браузере")} />
                                </a>
                            )}
                            {!game?.is_mini_browser && o.id && (
                                <a
                                    className="btn btn-sm btn-info"
                                    href={`https://vkplay.ru/app/${game.id}/lp/${o.num}`}
                                    target="_blank"
                                >
                                    <Awesome type="globe" tooltip={t("Открыть в браузере")} />
                                </a>
                            )}
                        </div>
                    );
                })}
            </FormFieldGroup>
        </>
    );
}

export function IframeLandingUrlsField({ apiData, setApiData }) {
    const { t } = useTranslation();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    if (!apiData?.forms_data) {
        return null;
    }
    const { game } = gameCommonInfo;

    const prefix = "main";
    const name = "iframe_landing_url";
    const form = apiData.forms_data[prefix];
    const field = form.fields[name];
    const widget = field?.widget || {};
    if (!field) {
        return null;
    }

    return field.widget.is_hidden ? (
        ""
    ) : (
        <>
            <FormFieldGroup label={field.label} required={field.required}>
                <div className="dev-inline-control">
                    <DevForm.TextInput
                        size="sm"
                        name={name}
                        value={form.data[name]}
                        placeholder={widget.attrs.placeholder}
                        minLength={field.min_length}
                        maxLength={field.max_length}
                        isInvalid={form.errors[name]}
                        disabled={field.disabled}
                        readOnly={widget.attrs?.readonly}
                        onChange={(ev) => fieldChange({ prefix, apiData, setApiData, name, val: ev.target.value })}
                    />
                    {game?.is_mini_browser && (
                        <a
                            className="btn btn-sm btn-info"
                            href={`https://mini.vkplay.ru/play/game/${game.slug}/lp/0/?draft=true`}
                            target="_blank"
                        >
                            <Awesome type="globe" tooltip={t("Открыть в браузере")} />
                        </a>
                    )}
                    {!game?.is_mini_browser && (
                        <a className="btn btn-sm btn-info" href={`https://vkplay.ru/app/${game.id}`} target="_blank">
                            <Awesome type="globe" tooltip={t("Открыть в браузере")} />
                        </a>
                    )}
                </div>
                <FormFieldHelpText help_text={field.help_text} />
            </FormFieldGroup>
        </>
    );
}

export function GameSlugField({ apiData, setApiData, helpText }) {
    if (!apiData?.forms_data) {
        return null;
    }

    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const { game } = gameCommonInfo;

    const prefix = "main";
    const name = "slug";
    const form = apiData.forms_data[prefix];
    const field = form.fields[name];
    if (!field) {
        return null;
    }
    const widget = field?.widget || {};
    const value = form.data[name];
    const sfx = "-" + game.id;

    let prependSlugUrl = "https://vkplay.ru/play/game/";
    if (game.is_mini_browser) {
        prependSlugUrl = "https://mini.vkplay.ru/play/game/";
    }

    return field.widget.is_hidden ? (
        ""
    ) : (
        <FormFieldGroup label={field.label} required={field.required}>
            <InputGroup>
                <InputGroup.Prepend>
                    <InputGroup.Text>{prependSlugUrl}</InputGroup.Text>
                </InputGroup.Prepend>
                <DevForm.TextInput
                    name={name}
                    value={value.replace(new RegExp(sfx + "$"), "")}
                    placeholder={widget.attrs.placeholder}
                    minLength={field.min_length}
                    maxLength={field.max_length}
                    isInvalid={form.errors[name]}
                    disabled={field.disabled}
                    readOnly={widget.attrs?.readonly}
                    onChange={(ev) => fieldChange({ prefix, apiData, setApiData, name, val: ev.target.value + sfx })}
                />
                <InputGroup.Append>
                    <InputGroup.Text>
                        {value.endsWith(sfx) ? sfx : ""}/<span style={{ width: "20px", color: "#c0bdbd" }}>|</span>
                        <Awesome
                            type="clipboard"
                            onClick={() => navigator.clipboard.writeText(prependSlugUrl + value + "/")}
                        />
                    </InputGroup.Text>
                </InputGroup.Append>
            </InputGroup>
            <FormFieldErrorMessage error={form.errors[name]} />
            <FormFieldHelpText help_text={helpText || field.help_text} />
        </FormFieldGroup>
    );
}

export function GameNameField({ apiData, setApiData, prefix }) {
    if (!apiData?.forms_data) {
        return null;
    }

    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const { game } = gameCommonInfo;

    const name = "name";
    const form = apiData.forms_data[prefix];
    const field = form.fields[name];
    if (!field) {
        return null;
    }
    const widget = field?.widget || {};
    const value = form.data[name] || "";
    const sfx = {
        ru_RU: " Тестирование",
        en_US: " Playtest",
    }[prefix];
    const mL = field.max_length - 15;

    if (field.widget.is_hidden) return "";

    if (game.is_playtest) {
        return (
            <FormFieldGroup label={field.label} required={field.required}>
                <InputGroup>
                    <DevForm.TextInput
                        name={name}
                        value={value.replace(new RegExp(sfx + "$"), "")}
                        placeholder={widget.attrs.placeholder}
                        minLength={field.min_length}
                        maxLength={mL}
                        isInvalid={form.errors[name]}
                        disabled={field.disabled}
                        readOnly={widget.attrs?.readonly}
                        onChange={(ev) =>
                            fieldChange({ prefix, apiData, setApiData, name, val: ev.target.value + sfx })
                        }
                    />
                    <InputGroup.Append>
                        <InputGroup.Text>{sfx}</InputGroup.Text>
                    </InputGroup.Append>
                </InputGroup>
                <FormFieldErrorMessage error={form.errors[name]} />
                <FormFieldLenCount maxLength={mL} disabled={field.disabled} val={value} />
                <FormFieldHelpText help_text={field.help_text} />
            </FormFieldGroup>
        );
    }

    return <DevForm.Field apiData={apiData} setApiData={setApiData} prefix={prefix} name={name} />;
}
